import { Box, type BoxProps, Collapse, Paper, Stack, type StackProps } from "@mui/material";
import { useResizeObserver } from "@root/utils/dom/useResizeObserver";
import { type ReactNode, useRef, useState } from "react";

export interface IPaperButtonCollapseProps {
  open?: boolean;
  transition?: number;
  label: ReactNode;
  children: ReactNode;
  onToggleOpen?: (open: boolean) => void;
  slotProps?: {
    box?: Partial<BoxProps>;
    labelStack?: Partial<StackProps>;
    collapseStack?: Partial<StackProps>;
  };
}

export const PaperButtonCollapse = ({
  open: controlledOpen,
  transition = 300,
  label,
  children,
  onToggleOpen,
  slotProps,
}: IPaperButtonCollapseProps) => {
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const boxContainerRef = useRef<HTMLDivElement>(null);
  const labelContainerRef = useRef<HTMLDivElement>(null);
  const boxContainerSize = useResizeObserver(boxContainerRef);
  const labelContainerSize = useResizeObserver(labelContainerRef);

  const toggleOpen = () => {
    if (controlledOpen === undefined) {
      setLocalOpen((localOpen) => !localOpen);
    }
    if (onToggleOpen) {
      onToggleOpen(!open);
    }
  };

  return (
    <Box {...slotProps?.box} ref={boxContainerRef}>
      <Paper
        variant="elevation"
        elevation={1}
        sx={{
          width: open ? boxContainerSize?.width : labelContainerSize?.width,
          ...(open && { borderRadius: 0 }),
          transition: `width ${transition}ms linear, border-radius ${transition}ms linear`,
        }}
      >
        <Stack
          onClick={toggleOpen}
          sx={{
            alignItems: "start",
            cursor: "pointer",
          }}
        >
          <Stack
            {...slotProps?.labelStack}
            ref={labelContainerRef}
            sx={{
              flex: "0 0 auto",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Stack>
        </Stack>
        <Collapse in={open} timeout={transition}>
          <Stack
            {...slotProps?.collapseStack}
            sx={{
              overflow: "hidden",
              width: boxContainerSize?.width,
            }}
          >
            {children}
          </Stack>
        </Collapse>
      </Paper>
    </Box>
  );
};
